/**
 * /me session-token helpers — pure, storage-light utilities the auth layer and
 * S11 invite share between them.
 *
 * Two distinct token shapes flow through the `client_session_token` store during
 * the shared-link self-select onboarding (WP-A contracts):
 *
 *   1. COUPLE token (pre-bind): minted by `POST /me/couple/auth`, carries
 *      `scope: "couple"` + `client_group_id`, binds NO person. It authorizes the
 *      least-privilege roster read (`/me/couple/preview`) + self-select
 *      (`/me/select-partner`) ONLY.
 *   2. PERSON token (post-bind): minted by `POST /me/select-partner`, carries
 *      `person_id`, binds the selected partner. It authorizes the full
 *      person-scoped onboarding (`/me/couple`, answers, consent, …).
 *
 * `MeShell` branches its couple-source on which one is held, so it needs to tell
 * them apart WITHOUT a network round-trip. We read the `scope`/`person_id` claim
 * off the JWT payload. (This is NOT the identity-decode the context warns
 * against: we never derive WHICH partner from the token — the server-authoritative
 * `acting_partner_id` still does that. We only read the token's SCOPE.)
 */

export type MeTokenBinding = "couple" | "person" | null; // pragma: allowlist secret

/** Decode a JWT's payload segment (base64url) without verifying the signature. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    // base64url → base64, then pad to a multiple of 4 for atob.
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Classify the held token as the couple-scoped (pre-bind) token or the
 * person-bound (post-bind) token. Anything undecodable defaults to `null`, and
 * `MeShell` treats null as the post-bind path — the safe default for a returning
 * user holding a legacy person token.
 */
export function getMeTokenBinding(token: string | null): MeTokenBinding {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  if (payload.scope === "couple") return "couple";
  if (payload.person_id != null) return "person";
  return null;
}

// ── Shared-link token stash (for the S11 invite) ─────────────────────────────
//
// The opener lands via `/me/auth?token=<shared-link-token>`. That opaque token
// is COUPLE-anchored — the backend mints ONE per couple and tolerates duplicate
// redemptions ("both partners opening the same link"; see
// `verify_couple_link_token`). So the SAME link the opener used is exactly what
// the second partner should open. We stash it at landing and reconstruct the
// share URL at S11. (It is the invite token, not the couple JWT — the JWT is the
// session credential, the stashed token is the durable, re-openable link.)

const ME_SHARE_LINK_TOKEN_KEY = "me_share_link_token"; // pragma: allowlist secret

export function stashShareLinkToken(token: string): void {
  localStorage.setItem(ME_SHARE_LINK_TOKEN_KEY, token);
}

export function getShareLinkToken(): string | null {
  return localStorage.getItem(ME_SHARE_LINK_TOKEN_KEY);
}

export function clearShareLinkToken(): void {
  localStorage.removeItem(ME_SHARE_LINK_TOKEN_KEY);
}

/**
 * Build the full shared-link URL the second partner opens. Returns null when no
 * shared-link token was stashed (e.g. a returning user who didn't land via the
 * shared link this session) so the caller can hide the invite affordance.
 */
export function buildShareLinkUrl(): string | null {
  const token = getShareLinkToken();
  if (!token) return null;
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/me/auth?token=${encodeURIComponent(token)}`;
}
